import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Trash2, Edit, Plus, X, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const Subjects = () => {
  const { t } = useTranslation('subjects');
  const { api, user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    subject_code: '',
    subject_name: '',
    category_id: '',
    is_optional: false,
    school_id: user?.school_id || 0
  });

  useEffect(() => {
    fetchSubjects();
    fetchCategories();
  }, []);

  const fetchSubjects = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/v1/subjects/');
      setSubjects(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching subjects:', err);
      setError(t('errors.failedToLoadSubjects'));
      toast.error(t('errors.failedToLoadSubjects'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/v1/subject-categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error(t('errors.failedToLoadCategories'));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : name === 'category_id' ? parseInt(value, 10) : value
    });
  };

  const resetForm = () => {
    setFormData({
      subject_code: '',
      subject_name: '',
      category_id: '',
      is_optional: false,
      school_id: user?.school_id || 0
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (subject) => {
    setSelectedSubject(subject);
    setFormData({
      subject_code: subject.subject_code,
      subject_name: subject.subject_name,
      category_id: subject.category_id,
      is_optional: subject.is_optional,
      school_id: subject.school_id
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (subject) => {
    setSelectedSubject(subject);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedSubject(null);
    resetForm();
  };

  const handleApiErrors = (error) => {
    if (error.response && error.response.data && error.response.data.detail) {
      const details = error.response.data.detail;
      const errors = Array.isArray(details) ? details.map(d => d.msg).join(', ') : details;
      toast.error(errors);
      return errors;
    }
    toast.error(t('errors.unexpectedError'));
    return t('errors.unexpectedError');
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/subjects/', formData);
      toast.success(t('messages.subjectAddedSuccess'));
      closeModals();
      fetchSubjects();
    } catch (err) {
      handleApiErrors(err);
    }
  };

  const handleEditSubject = async (e) => {
    e.preventDefault();
    if (!selectedSubject) return;

    try {
      await api.put(`/api/v1/subjects/${selectedSubject.id}`, formData);
      toast.success(t('messages.subjectUpdatedSuccess'));
      closeModals();
      fetchSubjects();
    } catch (err) {
      handleApiErrors(err);
    }
  };

  const handleDeleteSubject = async () => {
    if (!selectedSubject) return;

    try {
      await api.delete(`/api/v1/subjects/${selectedSubject.id}`);
      toast.success(t('messages.subjectDeletedSuccess'));
      closeModals();
      fetchSubjects();
    } catch (err) {
      handleApiErrors(err);
    }
  };

  // Loading state
  if (isLoading && subjects.length === 0) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E5252]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('title')}</h1>
        <button
          onClick={openAddModal}
          className="flex items-center bg-[#0E5252] hover:bg-teal-700 text-white px-4 py-2 rounded-md transition-colors"
        >
          <Plus size={18} className="sm:mr-1 sm:ml-1" />
          <span className="hidden sm:inline">{t('buttons.addSubject')}</span>
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex items-start">
          <AlertTriangle className="text-red-500 mt-0.5 mx-2 flex-shrink-0" size={18} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {subjects.length === 0 && !isLoading ? (
        <div className="text-center py-10 bg-gray-50 rounded-md">
          <p className="text-gray-500 mb-4">{t('messages.noSubjectsFound')}</p>
          <button
            onClick={openAddModal}
            className="bg-[#0E5252] hover:bg-teal-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            {t('buttons.addFirstSubject')}
          </button>
        </div>
      ) : (
        <div className="bg-white border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.code')}</th>
                  <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.name')}</th>
                  <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.category')}</th>
                  <th className="px-6 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.optional')}</th>
                  <th className="px-6 py-3 text-right rtl:text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subjects.map((subject) => (
                  <tr key={subject.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{subject.subject_code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{subject.subject_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {categories.find(c => c.id === subject.category_id)?.category_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {subject.is_optional ? t('common.yes') : t('common.no')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right rtl:text-left text-sm font-medium">
                      <div className="flex justify-end space-x-2 rtl:space-x-reverse">
                        <button
                          onClick={() => openEditModal(subject)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                          title={t('buttons.editSubject')}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(subject)}
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                          title={t('buttons.deleteSubject')}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Subject Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">{t('modals.addSubject.title')}</h2>
              <button onClick={closeModals} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddSubject}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.subjectCode')}
                  </label>
                  <input
                    type="text"
                    name="subject_code"
                    value={formData.subject_code}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.subjectName')}
                  </label>
                  <input
                    type="text"
                    name="subject_name"
                    value={formData.subject_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.category')}
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">{t('form.selectCategory')}</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_optional"
                    name="is_optional"
                    checked={formData.is_optional}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="is_optional" className="ml-2 rtl:mr-2 rtl:ml-0 block text-sm text-gray-700">
                    {t('form.optionalSubject')}
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end rtl:justify-start space-x-3 rtl:space-x-reverse">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0E5252] hover:bg-teal-700 text-white rounded-md"
                >
                  {t('buttons.addSubject')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Subject Modal */}
      {showEditModal && selectedSubject && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">{t('modals.editSubject.title')}</h2>
              <button onClick={closeModals} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSubject}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.subjectCode')}
                  </label>
                  <input
                    type="text"
                    name="subject_code"
                    value={formData.subject_code}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.subjectName')}
                  </label>
                  <input
                    type="text"
                    name="subject_name"
                    value={formData.subject_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('form.category')}
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">{t('form.selectCategory')}</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_optional_edit"
                    name="is_optional"
                    checked={formData.is_optional}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="is_optional_edit" className="ml-2 rtl:mr-2 rtl:ml-0 block text-sm text-gray-700">
                    {t('form.optionalSubject')}
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end rtl:justify-start space-x-3 rtl:space-x-reverse">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0E5252] hover:bg-teal-700 text-white rounded-md"
                >
                  {t('buttons.updateSubject')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedSubject && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">{t('modals.deleteSubject.title')}</h2>
              <button onClick={closeModals} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700">
                {t('modals.deleteSubject.confirmMessage')} <span className="font-semibold">{selectedSubject.subject_name}</span>? {t('modals.deleteSubject.warning')}
              </p>
            </div>
            <div className="flex justify-end rtl:justify-start space-x-3 rtl:space-x-reverse">
              <button
                onClick={closeModals}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={handleDeleteSubject}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
              >
                {t('buttons.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subjects;